import { Column } from 'primereact/column';
import { DataTableRowMouseEventParams } from 'primereact/datatable';
import React, { useCallback, useMemo, useRef } from 'react';
import Table from '../../../../components/Table/Table';
import { InputNumber } from 'primereact/inputnumber';
import ErrorToast from '../../../../components/messages/ErrorAlert';
import { Toast } from 'primereact/toast';
import { animateCellText } from '@/src/utils/htmlUtils';
import UM from './cells/UM';
import PhaseCode from './cells/PhaseCode';
import { formatCurrency } from '@/src/utils/formatUtils';
import Description from './cells/Description';
import { Tooltip } from 'primereact/tooltip';
import { SubcontractCOFormItem } from '../../../../Interfaces/Accounting/ACForms.interfaces';
import Currency from './cells/Currency';

type SLChangeOrderTableProps = {
  items: SubcontractCOFormItem[];
  defaultItems: React.MutableRefObject<Record<number, SubcontractCOFormItem>>;
  job: string;
  refreshItems: () => void;
  resetItem: (item: number) => void;
  addItem: () => void;
  removeItem: (item: number) => void;
  showErrors: boolean;
};

const SLChangeOrderTable = ({
  items,
  defaultItems,
  job,
  addItem,
  refreshItems,
  resetItem,
  removeItem,
  showErrors,
}: SLChangeOrderTableProps) => {
  const toast = useRef<Toast>(null);

  const onRowHover = (e: DataTableRowMouseEventParams) => {
    const cell = e.originalEvent.target as HTMLElement;
    const row = cell.closest('tr') as HTMLElement;
    animateCellText(row);
  };

  const updateItems = (item: SubcontractCOFormItem) => {
    const original = defaultItems.current[item.item];

    if (!item.isNew && original) {
      const hasChange = Object.keys(original).some((key) => {
        const originalValue = original[key as keyof SubcontractCOFormItem];
        const newValue = item[key as keyof SubcontractCOFormItem];

        return originalValue !== newValue && key !== 'isEdited';
      });

      item.isEdited = hasChange;
    }

    refreshItems();
  };

  let totalCosts = 0;
  items.forEach((item) => {
    totalCosts += item.total || 0;
  });

  return (
    <React.Fragment>
      <ErrorToast toastRef={toast} />
      <Table
        id="sl-co-items"
        data={useMemo(
          () => [
            ...items,
            {
              isNewLineOption: true,
              item: (items[items.length - 1]?.item ?? 0) + 1,
            },
          ],
          [items]
        )}
        dataKey="item"
        className={`mx-3 dashboardOptionShadow with-expanded noPadding noHover `}
        onRowMouseEnter={useCallback(onRowHover, [])}
        fixedDefaultHeight={790}
        hideColumns={false}
        rowClassName={(item) => (item.isNewLineOption ? 'printHide' : '')}
      >
        <Column
          body={(item) => {
            if (item.pending) {
              return (
                <React.Fragment>
                  <i
                    id={`pending-item-${item.item}`}
                    className="pi pi-spinner text-standard errorText printHide"
                    data-pr-position="left"
                    data-pr-tooltip="Item is pending an update so it cannot be modified!"
                  />
                  <Tooltip target={`#pending-item-${item.item}`} />
                  <span className="printMedia printShow">Pending Item</span>
                </React.Fragment>
              );
            }

            if (item.isNew) {
              return (
                <React.Fragment>
                  <i
                    id={`new-item-${item.item}`}
                    className="pi pi-plus-circle text-standard text-green-500 newItemRow printHide"
                    data-pr-position="left"
                    data-pr-tooltip="New Item"
                  />
                  <Tooltip target={`#new-item-${item.item}`} />
                  <span className="printMedia printShow">New Item</span>
                </React.Fragment>
              );
            }

            if (item.isEdited) {
              return (
                <React.Fragment>
                  <i
                    id={`edition-item-${item.item}`}
                    className="pi pi-pencil text-standard text-bluwaiBlue printHide"
                    data-pr-position="left"
                    data-pr-tooltip="Editing in progress"
                  />
                  <Tooltip target={`#edition-item-${item.item}`} />
                  <span className="printMedia printShow">Edited Item</span>
                </React.Fragment>
              );
            }

            return;
          }}
          style={{ minWidth: '70px', maxWidth: '70px' }}
          headerClassName={`tableHeader font-normal`}
          className={`text-standard blackText tableCell justify-content-center`}
          footerClassName="tableFooter"
        />
        <Column
          field="item"
          header="Item"
          body={(item) => {
            if (item.isNewLineOption) {
              return (
                <React.Fragment>
                  <i
                    id="new-item-option"
                    className="pi pi-plus text-standard cursor-pointer text-green-500"
                    onClick={() => {
                      addItem();
                    }}
                    data-pr-position="left"
                    data-pr-tooltip="Add new Item"
                  />
                  <Tooltip target="#new-item-option" />
                </React.Fragment>
              );
            }

            return item.item;
          }}
          style={{ minWidth: '100px', maxWidth: '100px' }}
          headerClassName="tableHeader font-normal text-center"
          className="justify-content-center text-standard blackText tableCell"
          footerClassName="tableFooter"
          sortable
        />
        <Column
          field="description"
          header="Description"
          body={(item) => {
            if (item.isNewLineOption) {
              return;
            }

            return (
              <Description
                item={item}
                updateItems={updateItems}
                showError={showErrors}
                disabled={item.pending}
              />
            );
          }}
          style={{ minWidth: '300px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter"
        />
        <Column
          field="phase"
          header="Phase Code"
          body={(item) => {
            if (item.isNewLineOption) {
              return;
            }

            return (
              <PhaseCode
                item={item}
                job={job}
                updateItems={updateItems}
                fetchData={false}
                showError={showErrors}
                disabled={item.pending}
              />
            );
          }}
          style={{ minWidth: '300px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter"
        />
        <Column
          field="um"
          header="UM"
          body={(item) => {
            if (item.isNewLineOption) {
              return;
            }

            return (
              <UM
                item={item}
                updateItems={updateItems}
                fetchData={false}
                showError={showErrors}
                disabled={item.pending}
              />
            );
          }}
          style={{ minWidth: '220px', maxWidth: '220px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="text-standard blackText tableCell"
          footerClassName="tableFooter"
        />
        <Column
          field="units"
          header="Current Units"
          body={(item) => {
            const hasError = showErrors && item.units == null;

            if (item.isNewLineOption) {
              return;
            }

            return (
              <React.Fragment>
                <InputNumber
                  min={0}
                  value={item.units}
                  className={`w-full ${hasError && 'p-invalid'} printHide`}
                  inputClassName="w-full blackText text-standard text-center"
                  onChange={(e) => {
                    item.units = e.value;
                  }}
                  onBlur={() => {
                    let total: number | undefined = undefined;

                    if (item.units && item.unitCosts) {
                      total = item.units * item.unitCosts;
                      total = Math.round(total * 100) / 100;
                    }

                    if (item.total !== total) {
                      item.total = total;
                      updateItems(item);
                    }
                  }}
                  disabled={item.pending || item.um === 'LS'}
                />
                <span className="printMedia printShow">{item.units}</span>
              </React.Fragment>
            );
          }}
          style={{ minWidth: '150px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="justify-content-center text-standard blackText tableCell"
          footerClassName="tableFooter"
        />
        <Column
          field="unitCosts"
          header="Current Unit Costs"
          body={(item) => {
            const hasError = showErrors && item.unitCosts == null;

            if (item.isNewLineOption) {
              return;
            }

            return (
              <React.Fragment>
                <Currency
                  defValue={item.unitCosts}
                  onChange={(val) => {
                    item.unitCosts = val;
                  }}
                  onBlur={() => {
                    let total: number | undefined = undefined;

                    if (item.units && item.unitCosts) {
                      total = item.units * item.unitCosts;
                      total = Math.round(total * 100) / 100;
                    }

                    if (item.total !== total) {
                      item.total = total;
                      updateItems(item);
                    }
                  }}
                  hasError={hasError}
                  disabled={!item.isNew || item.pending || item.um === 'LS'}
                />
                <span className="printMedia printShow">
                  {formatCurrency(item.unitCosts)}
                </span>
              </React.Fragment>
            );
          }}
          style={{ minWidth: '150px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="justify-content-center text-standard blackText tableCell"
          footer="Total"
          footerClassName="tableFooter block text-right border-top-2 border-transparent mt-4 limitBorder relative"
        />
        <Column
          field="total"
          header="Current Costs"
          body={(item) => {
            const hasError =
              showErrors &&
              (item.total == null || item.total < item.invoicedCost);

            if (item.isNewLineOption) {
              return;
            }

            return (
              <React.Fragment>
                <Currency
                  defValue={item.total}
                  onChange={(val) => {
                    item.total = val;
                  }}
                  onBlur={() => updateItems(item)}
                  hasError={hasError}
                  disabled={item.pending || item.um !== 'LS'}
                  errorMessage={
                    hasError && item.total < item.invoicedCost
                      ? `The Current Costs cannot be lower than the item's Invoiced Costs of $${item.invoicedCost}!`
                      : undefined
                  }
                />
                <span className="printMedia printShow">
                  {formatCurrency(item.total)}
                </span>
              </React.Fragment>
            );
          }}
          style={{ minWidth: '150px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="justify-content-end text-standard blackText tableCell"
          footer={formatCurrency(totalCosts)}
          footerClassName="tableFooter block text-right border-top-2 mt-4"
        />
        <Column
          field="retainage"
          header="Retainage"
          body={(item) => {
            const hasError = showErrors && item.retainage == null;

            if (item.isNewLineOption) {
              return;
            }

            return (
              <React.Fragment>
                <InputNumber
                  suffix="%"
                  min={0}
                  max={100}
                  maxFractionDigits={2}
                  value={item.retainage}
                  className={`w-full ${hasError && 'p-invalid'} printHide`}
                  inputClassName="w-full blackText text-standard text-center"
                  onChange={(e) => {
                    item.retainage = e.value;
                  }}
                  onBlur={() => {
                    updateItems(item);
                  }}
                  disabled={!item.isNew}
                />
                <span className="printMedia printShow">{item.retainage}%</span>
              </React.Fragment>
            );
          }}
          style={{ minWidth: '150px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="justify-content-center text-standard blackText tableCell"
          footerClassName="tableFooter"
        />
        <Column
          body={(item) => {
            if (item.isEdited) {
              return (
                <React.Fragment>
                  <i
                    id={`undo-item-${item.item}`}
                    className="pi pi-undo text-standard cursor-pointer"
                    onClick={() => {
                      resetItem(item.item);
                    }}
                    data-pr-position="left"
                    data-pr-tooltip="Reset item to original value"
                  />
                  <Tooltip target={`#undo-item-${item.item}`} />
                </React.Fragment>
              );
            }

            if (item.isNew) {
              return (
                <React.Fragment>
                  <i
                    id={`remove-item-${item.item}`}
                    className="pi pi-trash text-standard cursor-pointer"
                    onClick={() => {
                      removeItem(item.item);
                    }}
                    data-pr-position="left"
                    data-pr-tooltip="Remove Item"
                  />
                  <Tooltip target={`#remove-item-${item.item}`} />
                </React.Fragment>
              );
            }

            return;
          }}
          style={{ minWidth: '30px', maxWidth: '30px' }}
          headerClassName={`tableHeader font-normal`}
          className={`text-standard blackText printHide tableCell justify-content-center overflow-visible`}
          footerClassName="tableFooter"
        />
      </Table>
      <Tooltip target={`#newItem`} />
    </React.Fragment>
  );
};

export default SLChangeOrderTable;
